/**
 * Komal OS - KRA Template Store
 * Manages the KRA Library screen: templates, items, publish gate
 */

import { create } from 'zustand';
import * as kraTemplateService from '../services/kraTemplateService';

const useKraTemplateStore = create((set, get) => ({
  templates: [],
  selectedTemplate: null,
  total: 0,
  isLoading: false,
  isSubmitting: false,
  error: null,

  clearError: () => set({ error: null }),

  fetchTemplates: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await kraTemplateService.getKraTemplates(params);
      set({ templates: response.data.templates || [], total: response.data.total || 0, isLoading: false });
      return response.data.templates;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load KRA templates';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  fetchTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await kraTemplateService.getKraTemplate(id);
      set({ selectedTemplate: response.data.template, isLoading: false });
      return response.data.template;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load KRA template';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  createTemplate: async (data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.createKraTemplate(data);
      await get().fetchTemplates();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create template';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  addItem: async (templateId, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.addKraItem(templateId, data);
      await get().fetchTemplate(templateId);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add KRA item';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  updateItem: async (templateId, itemId, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.updateKraItem(templateId, itemId, data);
      await get().fetchTemplate(templateId);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update KRA item';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  deactivateItem: async (templateId, itemId) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.deactivateKraItem(templateId, itemId);
      await get().fetchTemplate(templateId);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove KRA item';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  publishTemplate: async (templateId) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.publishKraTemplate(templateId);
      await get().fetchTemplate(templateId);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      // Weightage/scoring-rule gate failures land here with a specific detail message
      const message = error.response?.data?.detail || 'Failed to publish template';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  deleteTemplate: async (templateId) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.deleteKraTemplate(templateId);
      await get().fetchTemplates();
      set({ selectedTemplate: null, isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete template';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  permanentlyDeleteTemplate: async (templateId) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.permanentlyDeleteKraTemplate(templateId);
      await get().fetchTemplates();
      set({ selectedTemplate: null, isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to permanently delete template';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  applyScoringRules: async (templateId, scoringRules) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.applyScoringRulesToAllItems(templateId, scoringRules);
      await get().fetchTemplate(templateId);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to apply scoring rules';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  cloneTemplate: async (templateId, data) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.cloneKraTemplate(templateId, data);
      await get().fetchTemplates();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to clone template';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  previewImport: async (file) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.importTemplatePreview(file);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to read the Excel file';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  commitImport: async (payload) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await kraTemplateService.importTemplateCommit(payload);
      await get().fetchTemplates();
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to import templates';
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },
}));

export default useKraTemplateStore;
